import { useCallback, useEffect, useMemo, useState } from "react";
import { ROOT_ROLE_UNIT, rolesRepo, type RoleUnit } from "@/services/roles.repository";
import { TENANT_BIZ, TENANT_KEYS, type BizKey, type TenantKey } from "@/domain/space";
import type { Role } from "@/domain/user";

/**
 * 한 사업부의 부서 목록 구독 + id→label 매핑(12 §16-2).
 * 생략하면 중앙 레미콘 — 계정 부서(claims role)·옛 사용자 관리 화면 기준(rules 루트 판정과 같음).
 */
export function useRoles(unit: RoleUnit = ROOT_ROLE_UNIT) {
  const { tenant, biz } = unit;
  const [state, setState] = useState<{ key: string; roles: Role[] } | null>(null);
  const key = `${tenant}/${biz}`;

  useEffect(
    () =>
      rolesRepo.watch(
        { tenant, biz },
        (roles) => setState({ key, roles }),
        () => setState({ key, roles: [] }),
      ),
    [tenant, biz, key],
  );

  // 사업부를 바꾼 직후 이전 목록을 보이지 않게
  const roles = useMemo(() => (state?.key === key ? state.roles : []), [state, key]);
  const labelById = useMemo(() => new Map(roles.map((r) => [r.id, r.label])), [roles]);
  return { roles, loading: state?.key !== key, labelById };
}

/** 모든 (법인, 사업부)의 부서 — 목록·상세에서 여러 사업부 부서 이름을 함께 보여 줄 때 */
export function useAllUnitRoles() {
  const [byUnit, setByUnit] = useState<Map<string, Role[]>>(() => new Map());

  useEffect(() => {
    const unsubs = TENANT_KEYS.flatMap((t) =>
      TENANT_BIZ[t].map((b) =>
        rolesRepo.watch({ tenant: t, biz: b }, (rows) => setByUnit((prev) => new Map(prev).set(`${t}/${b}`, rows))),
      ),
    );
    return () => unsubs.forEach((u) => u());
  }, []);

  return useMemo(() => {
    const rolesOf = (t: TenantKey, b: BizKey): Role[] => byUnit.get(`${t}/${b}`) ?? [];
    const labelOf = (t: TenantKey, b: BizKey, id: string): string => rolesOf(t, b).find((r) => r.id === id)?.label ?? id;
    const total = TENANT_KEYS.reduce((n, t) => n + TENANT_BIZ[t].length, 0);
    return { rolesOf, labelOf, loading: byUnit.size < total };
  }, [byUnit]);
}

export interface RoleOption { value: string; label: string }

/**
 * 사업부별 부서 선택지(UnitRolesEditor) — 그 사업부 부서 문서만(12 §16-2).
 * assignableOnly = 법인 관리자용(tenant_assignable 부서만, admin 제외). 전체 관리자는 false.
 */
export function useUnitRoleOptions(tenant: TenantKey, assignableOnly = false) {
  const { rolesOf, loading } = useAllUnitRoles();
  const optionsOf = useCallback(
    (b: BizKey): RoleOption[] =>
      rolesOf(tenant, b)
        .filter((r) => !assignableOnly || (r.tenantAssignable === true && r.id !== "admin"))
        .map((r) => ({ value: r.id, label: r.id === "admin" ? `${r.label} (전체 관리자)` : r.label })),
    [rolesOf, tenant, assignableOnly],
  );
  return { optionsOf, loading };
}
